'use client'

import {
  createContext,
  type Dispatch,
  type FC,
  type PropsWithChildren,
  type SetStateAction,
  useContext,
  useMemo,
} from 'react'

import {
  type OfferV11,
  type PimProductV11,
  type VolumeBasedPriceV1,
} from '@redteclab/api/clients/bully'

import { type AlgoliaProxyRecord, type BestOffer } from '../../../algolia'
import {
  type OpenableControls,
  useOpenableControls,
} from '../../../generic-hooks'

import { useProductQuantitySelectionControls } from './useProductQuantitySelectionControls'

type ProductVolumePricingContextValue = {
  currentOffer?: BestOffer | OfferV11
  currentVariant: PimProductV11 | AlgoliaProxyRecord
  openableControls: OpenableControls
  quantitySelectionControls: {
    quantity: number
    setQuantity: Dispatch<SetStateAction<number>>
  }
  volumeBasedPrices: VolumeBasedPriceV1[]
}

const ProductVolumePricingContext = createContext<
  ProductVolumePricingContextValue | undefined
>(undefined)

export const ProductVolumePricingContextProvider: FC<
  PropsWithChildren<{
    currentVariant: PimProductV11 | AlgoliaProxyRecord
    volumeBasedPrices: VolumeBasedPriceV1[]
  }>
> = ({ children, currentVariant, volumeBasedPrices }) => {
  const openableControls = useOpenableControls()

  const quantitySelectionControls = useProductQuantitySelectionControls({
    currentVariant,
  })

  const contextValue = useMemo(
    (): ProductVolumePricingContextValue => ({
      currentVariant,
      openableControls,
      quantitySelectionControls,
      volumeBasedPrices,
    }),
    [
      currentVariant,
      openableControls,
      quantitySelectionControls,
      volumeBasedPrices,
    ],
  )

  return (
    <ProductVolumePricingContext.Provider value={contextValue}>
      {children}
    </ProductVolumePricingContext.Provider>
  )
}

export const useProductVolumePricingContext =
  (): ProductVolumePricingContextValue => {
    const contextValue = useContext(ProductVolumePricingContext)

    if (contextValue === undefined) {
      throw new Error(
        'useProductVolumePricingContext must be used within a ProductVolumePricingContext',
      )
    }

    return contextValue
  }
